(() => {
  if (window.SpiritStockAwareFilters) return;

  const optionPrefix = 'filter.v.option.';
  const pageCache = new Map();
  let applyTimer;
  let isLoading = false;

  const normalise = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const getActiveOptionFilters = () => {
    const filters = new Map();
    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
      if (!key.startsWith(optionPrefix)) return;
      const option = normalise(key.slice(optionPrefix.length));
      if (!filters.has(option)) filters.set(option, new Set());
      filters.get(option).add(normalise(value));
    });

    return filters;
  };

  const parseVariantData = (card) => {
    const dataNode = card.querySelector('[data-stock-aware-variants]');
    if (!dataNode) return null;

    try {
      const payload = JSON.parse(dataNode.textContent);
      return {
        options: payload.options.map(normalise),
        variants: payload.variants.map((variant) => variant.map(normalise)),
      };
    } catch (error) {
      console.warn('Unable to read stock-aware variant data.', error);
      return null;
    }
  };

  const variantMatches = (variant, options, filters) => {
    for (const [filterOption, acceptedValues] of filters) {
      const optionIndex = options.indexOf(filterOption);
      if (optionIndex === -1 || !acceptedValues.has(variant[optionIndex])) return false;
    }
    return true;
  };

  const getContext = () => {
    const filterContainer = document.querySelector('filter-container');
    const section = filterContainer?.closest('[data-section-id]');
    const grid = filterContainer?.querySelector('.product-grid');
    const paginationRow = section?.querySelector('.pagination-row');

    if (!filterContainer || !section || !grid || !paginationRow) return null;
    return { filterContainer, section, grid, paginationRow };
  };

  const getBaseUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('stock_page');
    url.hash = '';
    return url;
  };

  const getNativeCurrentPage = () => {
    const page = Number(new URL(window.location.href).searchParams.get('page'));
    return Number.isInteger(page) && page > 0 ? page : 1;
  };

  const getCurrentStockPage = () => {
    const page = Number(new URL(window.location.href).searchParams.get('stock_page'));
    return Number.isInteger(page) && page > 0 ? page : 1;
  };

  const getNativePageCount = (paginationRow, currentPage) => {
    const pages = Array.from(paginationRow.querySelectorAll('a[href]'))
      .map((link) => Number(new URL(link.href, window.location.origin).searchParams.get('page')))
      .filter((page) => Number.isInteger(page) && page > 0);

    return Math.max(currentPage, ...pages, 1);
  };

  const getStockPageUrl = (baseUrl, page) => {
    const url = new URL(baseUrl);
    if (page > 1) url.searchParams.set('stock_page', page);
    return url;
  };

  const getCardsFromMarkup = (markup) => {
    const template = document.createElement('template');
    template.innerHTML = markup;
    return Array.from(template.content.querySelectorAll('product-block[data-stock-aware-product]'));
  };

  const getCurrentCards = (grid) => Array.from(grid.querySelectorAll('product-block[data-stock-aware-product]'))
    .map((card) => card.outerHTML);

  const getMatchingCards = (cards, filters) => cards.filter((markup) => {
    const card = getCardsFromMarkup(markup)[0];
    const data = card ? parseVariantData(card) : null;
    return !data || data.variants.some((variant) => variantMatches(variant, data.options, filters));
  });

  const fetchPageCards = async (baseUrl, page) => {
    const url = new URL(baseUrl);
    if (page > 1) url.searchParams.set('page', page);

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Unable to load filtered results (HTTP ${response.status}).`);

    const template = document.createElement('template');
    template.innerHTML = await response.text();
    const grid = template.content.querySelector('.product-grid');
    return grid ? getCurrentCards(grid) : [];
  };

  const ensureStockPage = async (result, requestedPage) => {
    const cardsNeeded = requestedPage * result.perPage;

    while (result.cards.length < cardsNeeded && result.exhausted === false) {
      if (result.nextNativePage > result.nativePageCount) {
        result.exhausted = true;
        break;
      }

      const nativePage = result.nextNativePage;
      let cards = result.pages.get(nativePage);
      if (!cards) {
        cards = await fetchPageCards(result.baseUrl, nativePage);
        result.pages.set(nativePage, cards);
      }

      result.cards.push(...getMatchingCards(cards, result.filters));
      result.nextNativePage += 1;
    }
  };

  const createPageLink = (baseUrl, page, label, className) => {
    const link = document.createElement('a');
    link.className = className;
    link.href = getStockPageUrl(baseUrl, page).href;
    link.textContent = label;
    return link;
  };

  const renderPagination = (paginationRow, result, currentPage) => {
    const pagination = document.createElement('div');
    pagination.className = 'pagination';

    if (currentPage > 1) {
      pagination.append(createPageLink(result.baseUrl, currentPage - 1, '\u00ab Previous', 'pagination__prev pagination__link text-current underline underline--on-hover'));
      pagination.append(document.createTextNode(' \u00b7 '));
    }

    const pageLabel = document.createElement('span');
    pageLabel.className = 'pagination__number underline underline--not-link';
    pageLabel.textContent = `Page ${currentPage}`;
    pagination.append(pageLabel);

    const hasNextPage = result.exhausted === false || result.cards.length > currentPage * result.perPage;
    if (hasNextPage) {
      pagination.append(document.createTextNode(' \u00b7 '));
      pagination.append(createPageLink(result.baseUrl, currentPage + 1, 'Next \u00bb', 'pagination__next pagination__link text-current underline underline--on-hover'));
    }

    paginationRow.replaceChildren(pagination);
  };

  const updateProductCount = (result) => {
    document.querySelectorAll('.utility-bar__centre').forEach((container) => {
      const countLabel = container.querySelector('.utility-bar__item');
      if (countLabel) {
        countLabel.textContent = result.exhausted
          ? `${result.cards.length} ${result.cards.length === 1 ? 'product' : 'products'}`
          : 'Showing in-stock results';
      }
    });
  };

  const renderPage = (context, result, requestedPage) => {
    const availablePages = Math.max(1, Math.ceil(result.cards.length / result.perPage));
    const currentPage = Math.min(Math.max(requestedPage, 1), availablePages);
    const start = (currentPage - 1) * result.perPage;

    context.grid.innerHTML = result.cards.slice(start, start + result.perPage).join('');
    context.grid.dataset.stockAwareSignature = `${result.key}|${currentPage}`;
    context.grid.classList.remove('stock-filter-loading');
    context.grid.removeAttribute('aria-busy');

    renderPagination(context.paginationRow, result, currentPage);
    updateProductCount(result);
    window.dispatchEvent(new CustomEvent('spirit:stock-filter-applied', {
      detail: { activeOptionFilters: result.filters.size, visibleCards: result.cards.length },
    }));
  };

  const createResult = (baseUrl, key, filters, nativePageCount, nativeCurrentPage, currentCards) => ({
    baseUrl,
    cards: [],
    exhausted: false,
    filters,
    key,
    nativePageCount,
    nextNativePage: 1,
    pages: new Map([[nativeCurrentPage, currentCards]]),
    perPage: currentCards.length,
  });

  const apply = async () => {
    if (isLoading) return;

    const filters = getActiveOptionFilters();
    if (filters.size === 0) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('stock_page')) {
        url.searchParams.delete('stock_page');
        window.history.replaceState({}, '', url);
      }
      return;
    }

    const context = getContext();
    if (!context) return;

    const baseUrl = getBaseUrl();
    const key = baseUrl.href;
    const requestedPage = getCurrentStockPage();
    const renderedSignature = `${key}|${requestedPage}`;
    if (context.grid.dataset.stockAwareSignature === renderedSignature) return;

    let result = pageCache.get(key);
    if (!result) {
      result = createResult(
        baseUrl,
        key,
        filters,
        getNativePageCount(context.paginationRow, getNativeCurrentPage()),
        getNativeCurrentPage(),
        getCurrentCards(context.grid),
      );
      pageCache.set(key, result);
    }

    isLoading = true;
    context.grid.classList.add('stock-filter-loading');
    context.grid.setAttribute('aria-busy', 'true');

    try {
      await ensureStockPage(result, requestedPage);
      if (getBaseUrl().href === key) renderPage(context, result, requestedPage);
    } catch (error) {
      context.grid.classList.remove('stock-filter-loading');
      context.grid.removeAttribute('aria-busy');
      console.warn('Unable to build stock-aware filtered results.', error);
    } finally {
      isLoading = false;
    }
  };

  const scheduleApply = () => {
    window.clearTimeout(applyTimer);
    applyTimer = window.setTimeout(apply, 20);
  };

  const start = () => {
    const context = getContext();
    if (!context) return;

    new MutationObserver(scheduleApply).observe(context.section, {
      childList: true,
      subtree: true,
    });
    window.addEventListener('popstate', scheduleApply);
    apply();
  };

  window.SpiritStockAwareFilters = { apply: scheduleApply };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
